import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  LayoutDashboard,
  Settings as SettingsIcon,
  ChevronLeft,
  ChevronRight,
  Zap,
  Sun,
  Moon,
  Users,
  Sparkles,
  Send,
  Puzzle,
  ExternalLink,
  LogOut,
  ArrowUpRight,
  Menu,
  SlidersHorizontal,
  BarChart3,
  CreditCard,
  Key,
  GraduationCap,
  BookOpen,
  FileCode2,
  Plug,
  Gauge,
  DollarSign,
  FlaskConical,
} from 'lucide-react';
import { useExtension } from '@/hooks/useExtension';
import { useAuth } from '@/hooks/useAuth';
import { apiGet } from '@/lib/api';

interface SidebarSubscriptionInfo {
  subscription: {
    planId: string;
    tokensUsed: number;
    tokensLimit: number;
  };
  plan: { name: string } | null;
}

interface SidebarUsageInfo {
  tokens: { used: number; limit: number; remaining: number };
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

const isAuthBypass = import.meta.env.VITE_AUTH_BYPASS === 'true';

export function Sidebar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { connected } = useExtension();
  const { user, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [theme, setTheme] = useState<'hypema-light' | 'hypema-dark'>('hypema-light');
  const [planName, setPlanName] = useState<string | null>(null);
  const [planId, setPlanId] = useState<string>('free');
  const [tokensUsed, setTokensUsed] = useState(0);
  const [tokensLimit, setTokensLimit] = useState(0);

  const isDark = theme === 'hypema-dark';

  // Outreach sub-nav: show 3 steps when inside outreach flow
  const inOutreach =
    pathname.startsWith('/leads') ||
    pathname.startsWith('/draft-emails') ||
    pathname.startsWith('/review-send');
  const outreachGroupMatch = pathname.match(/^\/(leads|draft-emails|review-send)\/([^/]+)/);
  const activeGroupId = outreachGroupMatch?.[2] || null;

  // Load theme
  useEffect(() => {
    const saved = localStorage.getItem('theme') as 'hypema-light' | 'hypema-dark' | null;
    if (saved) setTheme(saved);
    else setTheme('hypema-light');
  }, []);

  function toggleTheme() {
    const next = isDark ? 'hypema-light' : 'hypema-dark';
    setTheme(next);
    localStorage.setItem('theme', next);
    document.documentElement.setAttribute('data-theme', next);
  }

  // Persist sidebar state
  useEffect(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    if (saved) setCollapsed(saved === 'true');
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', String(collapsed));
  }, [collapsed]);

  // Keyboard shortcut: Ctrl/Cmd + B
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setCollapsed((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Mobile detection
  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) setMobileOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fetch billing/usage for sidebar widget
  const refreshUsage = useCallback(() => {
    Promise.all([
      apiGet<SidebarSubscriptionInfo>('/billing/subscription'),
      apiGet<SidebarUsageInfo>('/billing/usage'),
    ]).then(([sub, usg]) => {
      setPlanName(sub.plan?.name || sub.subscription.planId || 'Free');
      setPlanId(sub.subscription.planId || 'free');
      setTokensUsed(usg.tokens.used);
      setTokensLimit(usg.tokens.limit);
    }).catch(() => { /* silent — non-critical */ });
  }, []);

  useEffect(() => { refreshUsage(); }, [refreshUsage]);

  // Re-fetch when tokens are consumed by AI operations
  useEffect(() => {
    const handler = () => refreshUsage();
    window.addEventListener('tokens-changed', handler);
    return () => window.removeEventListener('tokens-changed', handler);
  }, [refreshUsage]);

  const unlimited = tokensLimit === -1;
  const usagePct = !unlimited && tokensLimit > 0 ? Math.min((tokensUsed / tokensLimit) * 100, 100) : 0;
  const usageColor = usagePct > 95 ? 'bg-error' : usagePct > 80 ? 'bg-warning' : 'bg-primary';

  // Theme-aware color classes
  const textPrimary = isDark ? 'text-white' : 'text-base-content';
  const textMuted = isDark ? 'text-white/60' : 'text-base-content/60';
  const textFaint = isDark ? 'text-white/40' : 'text-base-content/40';
  const hoverText = isDark ? 'hover:text-white' : 'hover:text-base-content';
  const hoverBg = isDark ? 'hover:bg-white/5' : 'hover:bg-base-content/5';
  const borderColor = isDark ? 'border-white/5' : 'border-base-300';
  const toggleBorder = isDark ? 'border-white/10' : 'border-base-300';
  const toggleText = isDark ? 'text-white/40 hover:text-white' : 'text-base-content/40 hover:text-base-content';

  // No campaign sub-nav needed

  function NavLink({ href, label, icon: Icon, active, sub = false }: {
    href: string; label: string; icon: typeof LayoutDashboard; active: boolean; sub?: boolean;
  }) {
    return (
      <Link
        to={href}
        className={`group flex items-center gap-3 rounded-lg text-sm transition-colors duration-150 ${
          sub ? 'px-2.5 py-1.5 ml-5' : 'px-2.5 py-2'
        } ${
          active
            ? `bg-primary/10 ${textPrimary} font-medium`
            : `${textMuted} ${hoverText} ${hoverBg}`
        }`}
        title={effectiveCollapsed ? label : undefined}
      >
        <Icon size={sub ? 14 : 18} className="shrink-0" />
        {!effectiveCollapsed && <span className={sub ? 'text-xs' : ''}>{label}</span>}
      </Link>
    );
  }

  // On mobile: sidebar is hidden unless mobileOpen. Show hamburger instead.
  const showSidebar = isMobile ? mobileOpen : true;
  const effectiveCollapsed = isMobile ? false : collapsed;

  return (
    <>
      {/* Mobile hamburger button */}
      {isMobile && !mobileOpen && (
        <button
          onClick={() => setMobileOpen(true)}
          className="fixed top-3 left-3 z-50 w-9 h-9 rounded-lg bg-base-100 border border-base-300 flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
        >
          <Menu size={18} className="text-base-content/60" />
        </button>
      )}

      {/* Mobile overlay backdrop */}
      {isMobile && mobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 animate-fade-in"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        className={`sidebar-bg flex flex-col h-screen transition-all duration-200 ease-out ${
          isMobile
            ? `fixed top-0 left-0 z-50 w-[220px] ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`
            : `sticky top-0 ${effectiveCollapsed ? 'w-14' : 'w-[220px]'}`
        }`}
      >
      {/* Logo */}
      <div className={`px-3 pt-3 pb-2 border-b ${borderColor}`}>
        <Link to="/dashboard" className="block min-w-0" onClick={() => isMobile && setMobileOpen(false)}>
          {effectiveCollapsed ? (
            <span className={`text-xl font-extrabold tracking-tight ${textPrimary}`}>H</span>
          ) : (
            <div>
              <span className={`text-xl font-extrabold tracking-tight ${textPrimary}`}>
                Hy<span className="text-primary">Pe</span>Ma
              </span>
              <p className={`text-[7px] font-semibold uppercase tracking-[0.15em] ${textFaint} mt-0.5`}>
                Hyper Personalized Mail
              </p>
            </div>
          )}
        </Link>
      </div>

      {/* Nav */}
      <nav className="flex-1 flex flex-col gap-0.5 px-2 py-3">
        {/* Workspace */}
        <NavLink
          href="/dashboard"
          label="Workspace"
          icon={LayoutDashboard}
          active={pathname === '/dashboard' || inOutreach}
        />

        {/* Outreach sub-nav (visible on workspace/dashboard or outreach flow) */}
        {(pathname === '/dashboard' || inOutreach) && !effectiveCollapsed && (
          <div className="flex flex-col gap-0.5 animate-fade-in">
            <NavLink
              href="/leads"
              label="Find Leads"
              icon={Users}
              active={pathname === '/leads'}
              sub
            />
            <NavLink
              href="/draft-emails"
              label="Draft Emails"
              icon={Sparkles}
              active={pathname.startsWith('/draft-emails')}
              sub
            />
            <NavLink
              href="/review-send"
              label="Review & Send"
              icon={Send}
              active={pathname.startsWith('/review-send')}
              sub
            />
          </div>
        )}

        {/* Settings */}
        <NavLink
          href="/settings"
          label="Settings"
          icon={SettingsIcon}
          active={pathname.startsWith('/settings')}
        />

        {/* Settings sub-nav */}
        {pathname.startsWith('/settings') && !effectiveCollapsed && (
          <div className="flex flex-col gap-0.5 animate-fade-in">
            <NavLink
              href="/settings"
              label="General"
              icon={SlidersHorizontal}
              active={pathname === '/settings'}
              sub
            />
            <NavLink
              href="/settings/usage"
              label="Usage"
              icon={BarChart3}
              active={pathname === '/settings/usage'}
              sub
            />
            <NavLink
              href="/settings/billing"
              label="Billing"
              icon={CreditCard}
              active={pathname === '/settings/billing'}
              sub
            />
            <NavLink
              href="/settings/api-keys"
              label="API Keys"
              icon={Key}
              active={pathname === '/settings/api-keys'}
              sub
            />
          </div>
        )}

        {/* HyPeMa Academy */}
        <NavLink
          href="/academy/guide"
          label="HyPeMa Academy"
          icon={GraduationCap}
          active={pathname.startsWith('/academy')}
        />
        {pathname.startsWith('/academy') && !effectiveCollapsed && (
          <div className="flex flex-col gap-0.5 animate-fade-in">
            <NavLink
              href="/academy/guide"
              label="Web App Guide"
              icon={BookOpen}
              active={pathname === '/academy/guide'}
              sub
            />
            <NavLink
              href="/academy/mcp"
              label="MCP Integration"
              icon={Plug}
              active={pathname === '/academy/mcp'}
              sub
            />
            <NavLink
              href="/academy/api-reference"
              label="API Reference"
              icon={FileCode2}
              active={pathname === '/academy/api-reference'}
              sub
            />
          </div>
        )}

        {/* Control Center (admin-only) */}
        {user?.role === 'admin' && (
          <>
            <NavLink
              href="/control-center/pricing"
              label="Control Center"
              icon={Gauge}
              active={pathname.startsWith('/control-center')}
            />
            {pathname.startsWith('/control-center') && !effectiveCollapsed && (
              <div className="flex flex-col gap-0.5 animate-fade-in">
                <NavLink
                  href="/control-center/pricing"
                  label="Pricing & Costs"
                  icon={DollarSign}
                  active={pathname === '/control-center/pricing'}
                  sub
                />
                <NavLink
                  href="/onboarding"
                  label="Test Wizard"
                  icon={FlaskConical}
                  active={false}
                  sub
                />
              </div>
            )}
          </>
        )}
      </nav>

      {/* Extension status */}
      {!effectiveCollapsed && connected === false && (
        <div className="px-3 pb-2">
          <div className="w-full cursor-pointer group" onClick={() => window.open('https://developer.chrome.com/docs/extensions/get-started/tutorial/hello-world#load-unpacked', '_blank')}>
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
              </span>
              <span className={`text-[10px] ${isDark ? 'text-white/50' : 'text-base-content/50'}`}>No Chrome extension found</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <ExternalLink size={12} className="text-primary/40 shrink-0 group-hover:translate-x-0.5 transition-transform" />
              <span className="text-[10px] font-medium text-primary group-hover:underline underline-offset-2">Click here to install Chrome extension</span>
            </div>
          </div>
        </div>
      )}
      {!effectiveCollapsed && connected === true && (
        <div className="px-3 pb-2">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-success shrink-0" />
            <span className={`text-[10px] ${textFaint}`}>Extension connected</span>
          </div>
        </div>
      )}

      {effectiveCollapsed && (
        <div className="px-3 pb-2 flex justify-center" title="Extension not installed — click to install">
          <div className="relative cursor-pointer">
            <div className={`w-8 h-8 rounded-full ${isDark ? 'bg-red-500/15' : 'bg-red-50'} flex items-center justify-center`}>
              <Puzzle size={14} className={isDark ? 'text-red-400' : 'text-red-500'} />
            </div>
            <span className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 border-2 ${isDark ? 'border-[#1a1a2e]' : 'border-base-100'}`} />
          </div>
        </div>
      )}


      {/* Usage widget */}
      {planName && !effectiveCollapsed && (
        <div className="mx-2 mb-2">
          <div onClick={() => navigate('/settings/usage')} className={`px-2.5 py-2 rounded-lg ${isDark ? 'bg-white/5 hover:bg-white/8' : 'bg-base-content/[0.03] hover:bg-base-content/[0.06]'} transition-colors group block cursor-pointer`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${textFaint}`}>{planName}</span>
              <div className="flex items-center gap-1.5">
                {planId === 'free' && (
                  <button
                    onClick={(e) => { e.stopPropagation(); navigate('/settings/billing'); }}
                    className="text-[9px] font-semibold text-primary hover:text-primary/80 transition-colors flex items-center gap-0.5"
                  >
                    Upgrade
                    <ArrowUpRight size={8} />
                  </button>
                )}
                {!unlimited && <span className={`text-[10px] tabular-nums ${textFaint}`}>{Math.round(usagePct)}%</span>}
              </div>
            </div>
            {!unlimited && (
              <div className={`w-full h-1 rounded-full ${isDark ? 'bg-white/10' : 'bg-base-content/10'} overflow-hidden`}>
                <div className={`h-full rounded-full ${usageColor} transition-all duration-500`} style={{ width: `${usagePct}%` }} />
              </div>
            )}
            <div className="flex items-center justify-between mt-1">
              <p className={`text-[10px] ${textFaint}`}>
                {unlimited ? `${formatTokens(tokensUsed)} tokens used` : `${formatTokens(tokensUsed)} / ${formatTokens(tokensLimit)} tokens`}
              </p>
              {!unlimited && usagePct > 80 && (
                <span className={`text-[9px] font-semibold ${usagePct > 95 ? 'text-error' : 'text-warning'}`}>
                  {usagePct > 95 ? 'Limit reached' : 'Running low'}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {planName && effectiveCollapsed && (
        <Link to="/settings/usage" className="flex justify-center pb-2" title={unlimited ? `${planName} — ${formatTokens(tokensUsed)} tokens used (unlimited)` : `${planName} — ${formatTokens(tokensUsed)} / ${formatTokens(tokensLimit)} tokens`}>
          <div className={`w-7 h-7 rounded-full ${isDark ? 'bg-white/5' : 'bg-base-content/[0.03]'} flex items-center justify-center relative hover:ring-2 hover:ring-primary/20 transition-all`}>
            {unlimited ? (
              <Zap size={12} className="text-primary" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" className="-rotate-90">
                <circle cx="10" cy="10" r="8" fill="none" stroke={isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'} strokeWidth="2" />
                <circle cx="10" cy="10" r="8" fill="none" className={usagePct > 95 ? 'stroke-error' : usagePct > 80 ? 'stroke-warning' : 'stroke-primary'} strokeWidth="2" strokeDasharray={`${usagePct * 0.5027} 50.27`} strokeLinecap="round" />
              </svg>
            )}
          </div>
        </Link>
      )}

      {/* User card */}
      <div className={`px-2 pb-3 border-t ${borderColor} pt-3`}>
        <div className={`flex items-center gap-2.5 ${effectiveCollapsed ? 'justify-center' : ''}`}>
          <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-primary text-sm font-bold shrink-0">
            {user?.email?.[0]?.toUpperCase() || '?'}
          </div>
          {!effectiveCollapsed && (
            <div className="min-w-0 flex-1">
              <p className={`text-sm ${textPrimary} font-medium truncate`}>
                {user?.email || 'Unknown'}
              </p>
              {isAuthBypass && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-warning/80">
                  <Zap size={10} />
                  Dev Mode
                </span>
              )}
            </div>
          )}
          {!effectiveCollapsed && (
            <div className="flex items-center gap-0.5">
              <button
                onClick={toggleTheme}
                className={`p-1.5 rounded-md ${textFaint} ${hoverText} ${hoverBg} transition-colors`}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDark ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                onClick={() => signOut()}
                className={`p-1.5 rounded-md ${textFaint} ${hoverText} ${hoverBg} transition-colors`}
                title="Sign out"
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
        {effectiveCollapsed && (
          <button
            onClick={toggleTheme}
            className={`mt-2 p-1.5 rounded-md ${textFaint} ${hoverText} ${hoverBg} transition-colors`}
            title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
        )}
      </div>

      {/* Collapse toggle (hidden on mobile) */}
      {!isMobile && (
        <button
          onClick={() => setCollapsed((prev) => !prev)}
          className={`absolute top-3 -right-3 w-6 h-6 rounded-full sidebar-bg border ${toggleBorder} flex items-center justify-center ${toggleText} transition-colors duration-150`}
          title={effectiveCollapsed ? 'Expand sidebar (Ctrl+B)' : 'Collapse sidebar (Ctrl+B)'}
        >
          {effectiveCollapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      )}
    </aside>
    </>
  );
}
